import React, { useState, useEffect, useCallback } from "react";
import {
  UserPlus,
  Link2,
  Copy,
  Check,
  Ban,
  RotateCcw,
  Clock,
  Shield,
  User,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  Mail,
  Send,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function InvitationsManager() {
  const [invitations, setInvitations] = useState([]);
  const [users, setUsers] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingInvitations, setLoadingInvitations] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [invitationsError, setInvitationsError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [email, setEmail] = useState("");
  const [lastGeneratedInvite, setLastGeneratedInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState(null);

  const fetchEmailStatus = useCallback(async () => {
    try {
      const status = await api.getInvitationEmailStatus();
      setEmailStatus(status);
    } catch {
      setEmailStatus(null);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    setUsersError(null);
    try {
      const userList = await api.listUsers();
      setUsers(Array.isArray(userList) ? userList : []);
    } catch (err) {
      const detail = err.message === "http_404"
        ? "El endpoint /api/auth/users devolvió 404. Verifica que el backend esté ejecutando la última versión."
        : err.message === "owner_required"
        ? "Se requieren permisos de Propietario para consultar el listado de miembros."
        : `Error al cargar usuarios: ${err.message || "desconocido"}`;
      setUsersError(detail);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    setLoadingInvitations(true);
    setInvitationsError(null);
    try {
      const invList = await api.listInvitations();
      setInvitations(Array.isArray(invList) ? invList : []);
    } catch (err) {
      const detail = err.message === "http_404"
        ? "El endpoint /api/auth/invitations devolvió 404. Verifica que el backend esté ejecutando la última versión."
        : `Error al cargar invitaciones: ${err.message || "desconocido"}`;
      setInvitationsError(detail);
    } finally {
      setLoadingInvitations(false);
    }
  }, []);

  const fetchData = useCallback(async () => {
    await Promise.all([fetchUsers(), fetchInvitations(), fetchEmailStatus()]);
  }, [fetchUsers, fetchInvitations, fetchEmailStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateInvite = async (e) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail || creating) return;

    setCreating(true);
    const idempotencyKey = `create_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    try {
      const res = await api.createInvitation({ email: cleanEmail }, { idempotencyKey });
      const inviteUrl = `${window.location.origin}/invite?token=${res.token}`;
      setLastGeneratedInvite({ ...res, fullUrl: inviteUrl });
      setEmail("");
      if (res.delivery_status === "accepted_by_provider") {
        toast.success(
          res.simulated
            ? "Invitación simulada generada exitosamente (modo local)"
            : "Invitación creada y aceptada por el proveedor de correo"
        );
      } else if (res.delivery_status === "uncertain") {
        toast.warning("Tiempo de espera con el proveedor. La operación quedó registrada para resolución segura.");
      } else if (res.delivery_status === "failed") {
        toast.warning("Invitación creada, pero falló el envío por correo. Copia el enlace manual.");
      } else {
        toast.success("Invitación creada exitosamente");
      }
      fetchInvitations();
    } catch (err) {
      if (err.message === "email_already_registered") {
        toast.error("Este correo ya está registrado en el estudio.");
      } else if (err.message === "invitation_dispatch_in_progress") {
        toast.warning("Ya hay una operación de invitación en curso para este correo.");
      } else {
        toast.error("Error al crear invitación: " + (err.message || "desconocido"));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleResendInvite = async (inv) => {
    if (resendingId) return;
    setResendingId(inv.id);
    const idempotencyKey = `resend_${inv.id}_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    try {
      const res = await api.resendInvitation(inv.id, { idempotencyKey });
      const inviteUrl = res.token ? `${window.location.origin}/invite?token=${res.token}` : lastGeneratedInvite?.fullUrl;
      setLastGeneratedInvite({ ...res, fullUrl: inviteUrl });
      if (res.delivery_status === "accepted_by_provider") {
        toast.success(
          res.simulated
            ? "Invitación simulada reenviada exitosamente (modo local)"
            : "Invitación reenviada y aceptada por Resend"
        );
      } else if (res.delivery_status === "uncertain") {
        toast.warning("Tiempo de espera al conectar con Resend. La operación fue conservada.");
      } else if (res.delivery_status === "failed") {
        toast.warning("Invitación renovada, pero el envío por correo reportó: " + (res.send_error || "Fallo"));
      } else {
        toast.success("Invitación renovada exitosamente");
      }
      fetchInvitations();
    } catch (err) {
      toast.error("Error al reenviar invitación: " + (err.message || "desconocido"));
    } finally {
      setResendingId(null);
    }
  };

  const handleCopyLink = () => {
    if (!lastGeneratedInvite?.fullUrl) return;
    navigator.clipboard.writeText(lastGeneratedInvite.fullUrl);
    setCopied(true);
    toast.success("Enlace de invitación copiado al portapapeles");
    setTimeout(() => setCopied(false), 2500);
  };

  const handleRevoke = async (invId) => {
    try {
      await api.revokeInvitation(invId);
      toast.success("Invitación revocada");
      fetchInvitations();
    } catch (err) {
      toast.error("No se pudo revocar la invitación: " + err.message);
    }
  };

  const handleToggleStatus = async (user) => {
    const nextStatus = user.status === "suspended" ? "active" : "suspended";
    setUpdatingUserId(user.id);
    try {
      await api.updateUserStatus(user.id, { status: nextStatus });
      toast.success(
        nextStatus === "suspended"
          ? `Acceso de ${user.name || user.email} suspendido`
          : `Acceso de ${user.name || user.email} reactivado`
      );
      fetchUsers();
    } catch (err) {
      toast.error("Error al actualizar estado del usuario: " + err.message);
    } finally {
      setUpdatingUserId(null);
    }
  };

  const getStatusBadge = (inv) => {
    if (inv.status === "accepted") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-400">
          <CheckCircle2 size={12} /> Aceptada
        </span>
      );
    }
    if (inv.status === "revoked" || inv.status === "replaced") {
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800/60 px-2.5 py-0.5 text-xs font-medium text-zinc-400">
          <Ban size={12} /> {inv.status === "replaced" ? "Reemplazada" : "Revocada"}
        </span>
      );
    }
    const isExpired = inv.expires_at && new Date(inv.expires_at) <= new Date();
    if (isExpired) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/30 bg-rose-500/10 px-2.5 py-0.5 text-xs font-medium text-rose-400">
          <Clock size={12} /> Caducada
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-[#f5b544]/40 bg-[#f5b544]/10 px-2.5 py-0.5 text-xs font-medium text-[#f5b544]">
        <Clock size={12} /> Pendiente
      </span>
    );
  };

  const getDeliveryBadge = (inv) => {
    if (inv.delivery_status === "accepted_by_provider") {
      return (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-400"
          title={inv.resend_id ? `ID de Resend: ${inv.resend_id}` : "Aceptado por Resend para entrega"}
        >
          <Mail size={11} /> Aceptado por Resend
        </span>
      );
    }
    if (inv.delivery_status === "uncertain") {
      return (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-400"
          title={inv.send_error || "Tiempo de espera agotado. Resultado incierto con el proveedor."}
        >
          <Clock size={11} /> Envío incierto (Timeout)
        </span>
      );
    }
    if (inv.delivery_status === "failed") {
      return (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-[11px] font-medium text-rose-400"
          title={inv.send_error || "Error al enviar correo"}
        >
          <AlertCircle size={11} /> Error de envío
        </span>
      );
    }
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800/60 px-2 py-0.5 text-[11px] font-medium text-zinc-400"
        title="Sin envío automático por correo"
      >
        <Link2 size={11} /> Enlace manual
      </span>
    );
  };

  const isGlobalLoading = loadingUsers || loadingInvitations;

  return (
    <div className="flex flex-col gap-6" data-testid="invitations-manager">
      {/* Sección 1: Crear Invitación */}
      <section className="v-panel p-5 md:p-6" data-testid="create-invitation-section">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="text-lg font-semibold text-[#f4f1ec]">Invitar Nuevo Colaborador</h2>
              {emailStatus?.simulated ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 px-2.5 py-0.5 text-[11px] font-medium text-sky-400">
                  <CheckCircle2 size={11} /> Correo simulado (Modo local)
                </span>
              ) : emailStatus?.configured ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-medium text-emerald-400">
                  <CheckCircle2 size={11} /> Correo activo (Resend)
                </span>
              ) : emailStatus ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-800/60 px-2.5 py-0.5 text-[11px] font-medium text-zinc-400">
                  <Link2 size={11} /> Modo manual (Sin Resend)
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-xs text-[#a3a3a3]">
              Envía una invitación oficial por correo o genera un enlace de un solo uso con caducidad de 48 horas.
              El invitado podrá registrarse con su contraseña y trabajar en su propio espacio independiente.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchData}
            title="Refrescar datos"
            className="v-btn-ghost v-btn-sm shrink-0"
          >
            <RefreshCw size={14} className={isGlobalLoading ? "animate-spin text-[#f5b544]" : ""} />
          </button>
        </div>

        <form onSubmit={handleCreateInvite} className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="email"
            required
            data-testid="invite-email-input"
            placeholder="correo@colaborador.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="v-input max-w-md flex-1 text-sm"
          />
          <button
            type="submit"
            disabled={creating || !email.trim()}
            data-testid="create-invite-btn"
            className="v-btn-primary v-btn-sm inline-flex items-center gap-2"
          >
            <UserPlus size={15} />
            {creating ? "Enviando..." : (emailStatus?.configured ? "Enviar Invitación" : "Generar Invitación")}
          </button>
        </form>

        {/* Modal / Alerta de Enlace Generado o Enviado */}
        {lastGeneratedInvite && (
          <div
            data-testid="invite-link-banner"
            className="mt-5 rounded-lg border border-[#f5b544]/40 bg-[#1c1810] p-4 text-xs"
          >
            <div className="flex items-start gap-3">
              {lastGeneratedInvite.delivery_status === "accepted_by_provider" ? (
                <Mail size={18} className="mt-0.5 shrink-0 text-emerald-400" />
              ) : lastGeneratedInvite.delivery_status === "uncertain" ? (
                <Clock size={18} className="mt-0.5 shrink-0 text-amber-400" />
              ) : (
                <Link2 size={18} className="mt-0.5 shrink-0 text-[#f5b544]" />
              )}
              <div className="flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-[#f5b544]">
                    {lastGeneratedInvite.send_count > 1 ? "¡Invitación reenviada" : "¡Invitación preparada"} para {lastGeneratedInvite.email}!
                  </span>
                  {lastGeneratedInvite.delivery_status === "accepted_by_provider" ? (
                    <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-400">
                      Aceptada por Resend
                    </span>
                  ) : lastGeneratedInvite.delivery_status === "uncertain" ? (
                    <span className="rounded-full border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-400">
                      Resultado incierto (Timeout)
                    </span>
                  ) : lastGeneratedInvite.delivery_status === "failed" ? (
                    <span className="rounded-full border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-[11px] font-medium text-rose-400">
                      Error al enviar correo
                    </span>
                  ) : null}
                </div>

                {lastGeneratedInvite.delivery_status === "accepted_by_provider" ? (
                  <p className="text-[#a3a3a3]">
                    El proveedor Resend ha aceptado el correo para su entrega en{" "}
                    <strong className="text-[#f4f1ec]">{lastGeneratedInvite.email}</strong>. Ten en cuenta que la
                    entrega final depende de los filtros y políticas del buzón destinatario.
                  </p>
                ) : lastGeneratedInvite.delivery_status === "uncertain" ? (
                  <p className="text-amber-300">
                    Se agotó el tiempo de espera al conectar con el proveedor de correo. La operación ha quedado
                    conservada para resolverla de forma segura. Puedes compartir el enlace alternativo inferior.
                  </p>
                ) : lastGeneratedInvite.delivery_status === "failed" ? (
                  <p className="text-rose-300">
                    No se pudo entregar a través del proveedor de correo ({lastGeneratedInvite.send_error || "error desconocido"}).
                    Puedes compartir manualmente el enlace directo alternativo inferior.
                  </p>
                ) : (
                  <p className="text-[#a3a3a3]">
                    Copia y envía este enlace personalmente al invitado. Por seguridad, este enlace es de un solo uso y
                    caduca en 48 horas.
                  </p>
                )}

                <p className="text-[#737373] text-[11px]">
                  Enlace de registro directo (un solo uso, caduca en 48h):
                </p>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={lastGeneratedInvite.fullUrl}
                    className="v-input flex-1 font-mono text-xs text-[#f5b544] select-all"
                  />
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    data-testid="copy-invite-link-btn"
                    className="v-btn-primary v-btn-sm inline-flex items-center gap-1.5 shrink-0"
                  >
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                    {copied ? "Copiado" : "Copiar Enlace"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Sección 2: Usuarios Registrados en el Estudio */}
      <section className="v-panel p-5 md:p-6" data-testid="registered-users-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-semibold text-[#f4f1ec]">Usuarios Registrados en el Estudio</h2>
              {!loadingUsers && !usersError && users.length > 0 && (
                <span className="rounded-full bg-[#202020] border border-[#2e2e2e] px-2.5 py-0.5 text-[11px] font-mono text-[#a3a3a3]">
                  {users.length} {users.length === 1 ? "cuenta" : "cuentas"}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-[#a3a3a3]">
              Cuentas con acceso a la plataforma. Puedes suspender o reactivar el acceso de los invitados sin borrar sus datos.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchUsers}
            disabled={loadingUsers}
            data-testid="refresh-users-btn"
            title="Actualizar listado de usuarios"
            className="v-btn-secondary v-btn-sm inline-flex items-center gap-1.5 self-start sm:self-auto"
          >
            <RefreshCw size={13} className={loadingUsers ? "animate-spin text-[#f5b544]" : ""} />
            <span>{loadingUsers ? "Actualizando..." : "Actualizar"}</span>
          </button>
        </div>

        {/* Estado: Cargando */}
        {loadingUsers && (
          <div
            className="mt-4 flex flex-col items-center justify-center rounded-lg border border-[#222] bg-[#121212] p-8 text-center"
            data-testid="users-loading-state"
          >
            <RefreshCw size={20} className="animate-spin text-[#f5b544] mb-2" />
            <p className="text-xs text-[#a3a3a3]">Cargando usuarios registrados en el estudio...</p>
          </div>
        )}

        {/* Estado: Error con opción de Reintento */}
        {!loadingUsers && usersError && (
          <div
            className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-xs"
            data-testid="users-error-state"
          >
            <div className="flex items-start gap-2.5 text-rose-300">
              <AlertCircle size={16} className="shrink-0 mt-0.5 text-rose-400" />
              <div>
                <p className="font-semibold text-rose-200">No se pudo cargar el listado de usuarios</p>
                <p className="mt-0.5 text-rose-300/80">{usersError}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={fetchUsers}
              data-testid="retry-users-btn"
              className="v-btn-secondary v-btn-sm shrink-0 self-start sm:self-auto inline-flex items-center gap-1.5 text-xs text-rose-200 hover:text-white"
            >
              <RotateCcw size={12} />
              <span>Reintentar</span>
            </button>
          </div>
        )}

        {/* Estado: Lista realmente vacía */}
        {!loadingUsers && !usersError && users.length === 0 && (
          <div
            className="mt-4 rounded-lg border border-[#222] bg-[#121212] p-8 text-center text-xs"
            data-testid="users-empty-state"
          >
            <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-[#1c1c1c] text-[#707070] mb-3">
              <User size={20} />
            </div>
            <p className="font-medium text-[#d4d4d4]">No hay usuarios registrados</p>
            <p className="mt-1 text-[#707070] max-w-sm mx-auto">
              Actualmente no se encontraron cuentas registradas en la base de datos local.
            </p>
          </div>
        )}

        {/* Estado: Lista con Datos */}
        {!loadingUsers && !usersError && users.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-[#262626] text-[#a3a3a3]">
                  <th className="py-2.5 font-medium">Nombre</th>
                  <th className="py-2.5 font-medium">Correo</th>
                  <th className="py-2.5 font-medium">Rol</th>
                  <th className="py-2.5 font-medium">Estado</th>
                  <th className="py-2.5 font-medium">Registro</th>
                  <th className="py-2.5 text-right font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1e1e1e]">
                {users.map((u) => {
                  const isOwner = u.role === "owner" || u.id === "owner" || u._id === "owner";
                  const isSuspended = u.status === "suspended";
                  return (
                    <tr key={u.id || u.email} className="hover:bg-[#151515]">
                      <td className="py-3 font-medium text-[#f4f1ec]">
                        <div className="flex items-center gap-2">
                          {isOwner ? (
                            <Shield size={14} className="text-[#f5b544]" />
                          ) : (
                            <User size={14} className="text-[#a3a3a3]" />
                          )}
                          <span>{u.name || (isOwner ? "Propietario del Estudio" : "Sin nombre")}</span>
                        </div>
                      </td>
                      <td className="py-3 text-[#a3a3a3] font-mono">{u.email}</td>
                      <td className="py-3">
                        {isOwner ? (
                          <span className="rounded bg-[#2a2210] border border-[#f5b544]/30 px-2 py-0.5 text-[11px] font-semibold text-[#f5b544]">
                            Propietario
                          </span>
                        ) : (
                          <span className="rounded bg-[#222] border border-[#333] px-2 py-0.5 text-[11px] font-medium text-[#a3a3a3]">
                            Invitado
                          </span>
                        )}
                      </td>
                      <td className="py-3">
                        {isSuspended ? (
                          <span className="inline-flex items-center gap-1 text-rose-400">
                            <Ban size={12} /> Suspendido
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-400">
                            <CheckCircle2 size={12} /> Activo
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-[#707070]">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="py-3 text-right">
                        {isOwner ? (
                          <span className="text-[11px] text-[#707070] italic">Cuenta principal</span>
                        ) : (
                          <button
                            type="button"
                            disabled={updatingUserId === u.id}
                            onClick={() => handleToggleStatus(u)}
                            className={`v-btn-ghost v-btn-sm text-xs ${
                              isSuspended ? "text-emerald-400 hover:text-emerald-300" : "text-rose-400 hover:text-rose-300"
                            }`}
                          >
                            {isSuspended ? (
                              <>
                                <RotateCcw size={13} className="mr-1 inline" /> Reactivar
                              </>
                            ) : (
                              <>
                                <Ban size={13} className="mr-1 inline" /> Suspender
                              </>
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Sección 3: Historial de Invitaciones */}
      <section className="v-panel p-5 md:p-6" data-testid="invitations-history-section">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5">
              <h2 className="text-lg font-semibold text-[#f4f1ec]">Historial de Invitaciones</h2>
              {!loadingInvitations && !invitationsError && invitations.length > 0 && (
                <span className="rounded-full bg-[#202020] border border-[#2e2e2e] px-2.5 py-0.5 text-[11px] font-mono text-[#a3a3a3]">
                  {invitations.length} {invitations.length === 1 ? "invitación" : "invitaciones"}
                </span>
              )}
            </div>
            <p className="mt-1 text-xs text-[#a3a3a3]">
              Estado de los enlaces de invitación emitidos. Solo las pendientes pueden revocarse o aceptarse.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchInvitations}
            disabled={loadingInvitations}
            data-testid="refresh-invitations-btn"
            title="Actualizar invitaciones"
            className="v-btn-secondary v-btn-sm inline-flex items-center gap-1.5 self-start sm:self-auto"
          >
            <RefreshCw size={13} className={loadingInvitations ? "animate-spin text-[#f5b544]" : ""} />
            <span>{loadingInvitations ? "Actualizando..." : "Actualizar"}</span>
          </button>
        </div>

        {/* Estado: Cargando invitaciones */}
        {loadingInvitations && (
          <div
            className="mt-4 flex flex-col items-center justify-center rounded-lg border border-[#222] bg-[#121212] p-8 text-center"
            data-testid="invitations-loading-state"
          >
            <RefreshCw size={20} className="animate-spin text-[#f5b544] mb-2" />
            <p className="text-xs text-[#a3a3a3]">Cargando historial de invitaciones...</p>
          </div>
        )}

        {/* Estado: Error en invitaciones */}
        {!loadingInvitations && invitationsError && (
          <div
            className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-xs"
            data-testid="invitations-error-state"
          >
            <div className="flex items-start gap-2.5 text-rose-300">
              <AlertCircle size={16} className="shrink-0 mt-0.5 text-rose-400" />
              <div>
                <p className="font-semibold text-rose-200">No se pudo cargar el historial de invitaciones</p>
                <p className="mt-0.5 text-rose-300/80">{invitationsError}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={fetchInvitations}
              data-testid="retry-invitations-btn"
              className="v-btn-secondary v-btn-sm shrink-0 self-start sm:self-auto inline-flex items-center gap-1.5 text-xs text-rose-200 hover:text-white"
            >
              <RotateCcw size={12} />
              <span>Reintentar</span>
            </button>
          </div>
        )}

        {/* Estado: Sin invitaciones */}
        {!loadingInvitations && !invitationsError && invitations.length === 0 && (
          <div
            className="mt-4 rounded-lg border border-[#222] bg-[#121212] p-6 text-center text-xs text-[#707070]"
            data-testid="invitations-empty-state"
          >
            No hay invitaciones registradas aún.
          </div>
        )}

        {/* Estado: Tabla de invitaciones */}
        {!loadingInvitations && !invitationsError && invitations.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-[#262626] text-[#a3a3a3]">
                  <th className="py-2.5 font-medium">Correo Destinatario</th>
                  <th className="py-2.5 font-medium">Estado</th>
                  <th className="py-2.5 font-medium">Envío</th>
                  <th className="py-2.5 font-medium">Emitida</th>
                  <th className="py-2.5 font-medium">Caducidad (48h)</th>
                  <th className="py-2.5 text-right font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1e1e1e]">
                {invitations.map((inv) => {
                  const isPending = inv.status === "pending" && new Date(inv.expires_at) > new Date();
                  const isResending = resendingId === inv.id;
                  return (
                    <tr key={inv.id} className="hover:bg-[#151515]">
                      <td className="py-3 font-mono font-medium text-[#f4f1ec]">{inv.email}</td>
                      <td className="py-3">{getStatusBadge(inv)}</td>
                      <td className="py-3">{getDeliveryBadge(inv)}</td>
                      <td className="py-3 text-[#707070]">
                        {inv.created_at ? new Date(inv.created_at).toLocaleString() : "—"}
                      </td>
                      <td className="py-3 text-[#707070]">
                        {inv.expires_at ? new Date(inv.expires_at).toLocaleString() : "—"}
                      </td>
                      <td className="py-3 text-right">
                        {isPending && (
                          <div className="inline-flex items-center gap-2">
                            <button
                              type="button"
                              disabled={isResending}
                              onClick={() => handleResendInvite(inv)}
                              data-testid={`resend-invite-btn-${inv.id}`}
                              title="Reenviar correo de invitación (renueva el enlace)"
                              className="v-btn-ghost v-btn-sm text-xs text-[#f5b544] hover:text-[#ffd27a] inline-flex items-center gap-1"
                            >
                              <RefreshCw size={11} className={isResending ? "animate-spin" : ""} />
                              <span>{isResending ? "Reenviando..." : "Reenviar correo"}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRevoke(inv.id)}
                              className="v-btn-ghost v-btn-sm text-xs text-rose-400 hover:text-rose-300"
                            >
                              Revocar
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
